import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { LockClosedIcon } from '@heroicons/react/20/solid';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const FormSwitch = ({
  name,
  label,
  subtitle,
  handleChange,
  options = {},
  disabled,
  disabledText,
  checked: controlledChecked,
  onChange: controlledOnChange,
  labelStyle,
  subtitleStyle,
  switchStyle = {}
}) => {
  const [checked, setChecked] = useState(
    controlledChecked !== undefined 
      ? controlledChecked 
      : options?.[name] || false
  );
  const [showDisabledMessage, setShowDisabledMessage] = useState(false);

  useEffect(() => {
    if (controlledChecked !== undefined) {
      setChecked(controlledChecked);
    }
  }, [controlledChecked]);

  const handleSwitchChange = (enabled) => {
    if (disabled) {
      setShowDisabledMessage(true);
      setTimeout(() => setShowDisabledMessage(false), 6000);
      return;
    }

    setChecked(enabled);
    
    if (controlledOnChange) {
      controlledOnChange(enabled);
    } else if (handleChange) {
      const event = {
        target: {
          name: name,
          value: enabled,
        },
      };
      handleChange(event);
    }
  };

  // Default styles that can be overridden
  const defaultLabelStyle = { 
    color: '#F1C40F',
    fontFamily: '"Share Tech Mono", monospace',
    fontWeight: 'bold',
    letterSpacing: '0.05em'
  };
  const defaultSubtitleStyle = { 
    color: '#95A5A6',
    fontFamily: '"Share Tech Mono", monospace',
    fontWeight: 'bold',
    letterSpacing: '0.05em'
  };
  const defaultSwitchEnabledColor = '#F1C40F';
  const defaultSwitchDisabledColor = 'rgba(52, 73, 94, 0.6)';

  return (
    <div
      className={classNames(
        'flex flex-col',
        disabled && 'opacity-75 pointer-events-none'
      )}
      style={{fontFamily: '"Share Tech Mono", monospace'}}
    >
      <div className="flex items-start justify-between gap-4">
        <span className="flex flex-grow flex-col min-w-0">
          <div className="flex items-center">
            {disabled && name === 'hideBranding' && (
              <span
                className={classNames(
                  'text-xs font-bold px-2 py-1 rounded-lg inline-flex items-center capitalize mr-2 tracking-wide'
                )}
                style={{
                  background: 'linear-gradient(135deg, #E67E22, #D35400)',
                  color: '#FFFFFF',
                  border: '1px solid rgba(230, 126, 34, 0.6)',
                }}
              >
                PREMIUM
                <LockClosedIcon className="ml-1 h-3 w-3" aria-hidden="true" />
              </span>
            )}
            <label 
              className="text-sm font-bold leading-6 tracking-wide"
              style={labelStyle || defaultLabelStyle}
            >
              {label}
            </label>
          </div>
          <span 
            className="text-sm break-words font-bold tracking-wide mt-1"
            style={subtitleStyle || defaultSubtitleStyle}
          >
            {subtitle}
          </span>
        </span>
        <Switch
          checked={checked}
          onChange={handleSwitchChange}
          className={classNames(
            'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 transition-all duration-200 ease-in-out focus:outline-none',
            disabled && 'pointer-events-none'
          )}
          style={{
            backgroundColor: checked 
              ? (switchStyle.enabledColor || defaultSwitchEnabledColor)
              : (switchStyle.disabledColor || defaultSwitchDisabledColor),
            borderColor: 'rgba(241, 196, 15, 0.4)',
            boxShadow: checked 
              ? '0 0 8px rgba(241, 196, 15, 0.3)' 
              : '0 2px 4px rgba(0, 0, 0, 0.2)'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = 'rgba(241, 196, 15, 0.8)';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = 'rgba(241, 196, 15, 0.4)';
          }}
        >
          <span
            aria-hidden="true"
            className={classNames(
              checked ? 'translate-x-5' : 'translate-x-0',
              'pointer-events-none inline-block h-5 w-5 transform rounded-full shadow ring-0 transition duration-200 ease-in-out',
            )}
            style={{
              backgroundColor: checked ? '#2C3E50' : '#ECF0F1',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
            }}
          />
        </Switch>
      </div>
      {showDisabledMessage && (
        <div className="text-xs font-bold tracking-wide mt-2" style={{
          color: '#E74C3C',
        }}>
          {disabledText}
        </div>
      )}
    </div>
  );
};

export default FormSwitch;